import { ArrowBackIcon } from "@chakra-ui/icons";
import { IconButton, VStack, Button } from "@chakra-ui/react";
import { SylaApiError } from "@syla/shared/errors/SylaApiError";
import { DataSourceType } from "@syla/shared/types/models/DataSourceBase";
import { PopulatedWallet } from "@syla/shared/types/models/WalletBase";
import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { FileDropZoneInput } from "../../../forms/FileDropZoneInput";
import { DataSourceDrawerFooter } from "../components/DataSourceDrawerFooter";
import { DataSourceDrawHeader } from "../components/DataSourceDrawerHeader";
import { DataSourceTitle } from "../components/DataSourceTitle";
import { ImportErrorAlert } from "../components/ImportErrorAlert";
import { ImportMethodHeading } from "../components/ImportMethodHeading";
import { InstructionsSection } from "../components/InstructionsSection";
import { FileImportMethod } from "../helpers/getSupportedFileImportMethods";
import { FileImportForm } from "../workflows/FileImportWorkflow";

/**
 * Do another file import for an existing data source, regardless of whether
 * it is an API, CSV or Custom data source.
 */
export function FileImportPanel({
  wallet,
  customName,
  error,
  clearError,
  fileImportMethod,
  onBack,
  onImportStart,
}: {
  wallet: PopulatedWallet;
  customName: string;
  error: Error | SylaApiError | unknown;
  clearError: () => void;
  fileImportMethod: FileImportMethod;
  onBack: () => void;
  onImportStart: () => void;
}): React.ReactElement {
  const form = useFormContext<FileImportForm>();

  return (
    <>
      <DataSourceDrawHeader>
        <IconButton
          variant="ghost"
          aria-label="Back"
          icon={<ArrowBackIcon />}
          boxSize="2.5rem"
          fontSize="2xl"
          onClick={onBack}
        />
        <DataSourceTitle name={customName} image={wallet.dataSource.image} />
      </DataSourceDrawHeader>

      <VStack
        w="full"
        px={4}
        py={4}
        overflowY="auto"
        flex={1}
        align="flex-start"
        spacing={0}
        gap={8}
      >
        <ImportErrorAlert
          error={error}
          isCustomDataSource={wallet.dataSource.type === DataSourceType.Custom}
          onClose={clearError}
        />

        <InstructionsSection
          w="full"
          dataSource={wallet.dataSource}
          importMethod={fileImportMethod}
          isNewDataSource={false}
        />

        <VStack
          w="full"
          align="flex-start"
          id="import-file-form"
          as="form"
          onSubmit={onImportStart}
          noValidate
          spacing={4}
        >
          <ImportMethodHeading
            dataSource={wallet.dataSource}
            method={fileImportMethod}
          />

          <Controller
            control={form.control}
            name="storedFiles"
            render={({ field }) => (
              <FileDropZoneInput
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </VStack>
      </VStack>

      <DataSourceDrawerFooter>
        <Button variant="outline" colorScheme="gray" onClick={onBack}>
          Back
        </Button>
        <Button
          form="import-file-form"
          variant="solid"
          colorScheme="primary"
          isDisabled={!form.formState.isDirty}
          type="submit"
        >
          Import
        </Button>
      </DataSourceDrawerFooter>
    </>
  );
}
